import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'antd/dist/antd.css';
import { Button, Carousel } from 'antd';
import 'remixicon/fonts/remixicon.css';
import '../src/vendor/bootstrap/css/bootstrap.min.css';
import '../src/vendor/bootstrap-icons/bootstrap-icons.css';
import '../src/css/style.css';
import {
  RightOutlined,
  FacebookOutlined,
  TwitterOutlined,
  InstagramOutlined,
  GooglePlusOutlined,
  LinkedinOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CarOutlined,
  HomeOutlined,
  CloseCircleFilled,
  EnvironmentOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import SupportWidget from './components/SupportWidget';

function Home() {
  const [parcelData, setParcelData] = useState(null);
  const [tracking_num, setTrackingNum] = useState('');
  const [tracking, setTracking] = useState('');
  const [tracking_status_show, setTrackingStatusShow] = useState(false);
  const [mission_vision, setMissionVision] = useState(true);
  const [core_value, setCoreValue] = useState(false);
  const [history, setHistory] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [trackingContent, setTrackingContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);

  useEffect(() => {
    if (!parcelData) {
      setTrackingStatusShow(false);
      setShowAlert(false);
    } else {
      setTrackingStatusShow(true);
      if (tracking.startsWith("BPICC") || tracking.startsWith("ROBCC")) {
        setShowAlert(true);
      }
      else {
        setShowAlert(false);
      }
    }
  }, [parcelData,tracking]);

  // Fetch tracking content from API
  useEffect(() => {
    const fetchTrackingContent = async () => {
      try {
        setContentLoading(true);
        const response = await axios.get("https://api.speedworkscourier.com/v2/tracking/");
        setTrackingContent(response.data.data);
        setContentLoading(false);
      } catch (error) {
        console.error('Error fetching tracking content:', error);
        setContentLoading(false);
        // Keep using default content if API fails
        setTrackingContent({
          creditCardNote: "Please note: For credit card deliveries, the bank does not provide us with your contact number. As a result, we are unable to notify you directly about your delivery status.",
          contactEmail: "info@speedworkscourier.com",
          contactPhones: ["02 8952-7411", "02 8806-8353"],
          contactFacebook: "https://www.facebook.com/speedworkscourier"
        });
      }
    };

    fetchTrackingContent();
  }, []);

  // Hero section tracking handler (shows results in modal)
  const handleHeroSubmit = async (event) => {
    try {
      event.preventDefault()

      if (!tracking_num.trim()) return; // Prevent empty submissions

      setTracking(tracking_num)
      setIsLoadingTracking(true);
      setShowModal(true);
      setParcelData(null); // Clear previous results

      const data = await axios.get(
        `https://api.speedworkscourier.com/v2/parcels/tracking?search=${tracking_num}`
      );

      // FIXED 2026-09-07 — customer complaint: rider mismarked a delivery
      // Delivered, corrected it to Failed 2 minutes later, but the website
      // kept showing Delivered. Root cause was two compounding bugs, both
      // built on the false assumption "Delivered is always genuinely final":
      //   1. Sorting truncated to calendar-day, then broke same-day ties by
      //      a hardcoded status-priority order that always ranked Delivered
      //      above Failed — so a same-day correction could get sorted
      //      BEFORE the mistake it was correcting, regardless of the real
      //      few-minutes-apart timestamps.
      //   2. A second pass then threw away anything "after" the first
      //      Delivered entry it found in that now-wrongly-ordered list —
      //      silently deleting the real correction.
      // Fixed by sorting strictly on the actual timestamp (no truncation, no
      // hardcoded status priority) and removing the "nothing after
      // Delivered" filter entirely — a correctly time-sorted list already
      // reflects reality with no need to special-case any one status as an
      // assumed dead end (deliveries CAN and do go Delivered → Failed).

      // Drop invalid/internal-only rows first — epoch dates (1970-01-01) are
      // corrupted records, and Dispatch (9) is internal-only, not for
      // customers.
      const rawItems = (data?.data?.data || []).filter(item => {
        const dateOnly = new Date(item.date).toISOString().split('T')[0];
        if (dateOnly === '1970-01-01') return false;
        if (item.status_delivery === 9) return false;
        return true;
      });

      // Sort by the REAL timestamp, newest first. This is the one and only
      // source of truth for "what happened when" — no day-truncation, no
      // assumption that any particular status outranks another.
      rawItems.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Some statuses can legitimately repeat (up to 3 delivery attempts per
      // day); others should only ever show their most recent occurrence.
      // Since rawItems is now correctly newest-first, "first occurrence
      // encountered" below IS "most recent occurrence" — this also fixes a
      // related bug where the old dedup ran BEFORE sorting and could keep a
      // stale FIRST-seen entry instead of the true latest one.
      const multipleOccurrenceStatuses = [3, 5]; // Out for Delivery, Delivery Failed — up to 3x/day
      const seenStatuses = new Set();
      const statusDatesSeen = new Set();
      const statusCounts = new Map();
      const uniqueData = [];

      rawItems.forEach(item => {
        const statusKey = item.status_delivery;
        if (multipleOccurrenceStatuses.includes(statusKey)) {
          const dateOnly = new Date(item.date).toISOString().split('T')[0];
          const statusDateKey = `${statusKey}_${dateOnly}`;
          if (!statusDatesSeen.has(statusDateKey)) {
            const count = statusCounts.get(statusKey) || 0;
            if (count < 3) {
              statusCounts.set(statusKey, count + 1);
              statusDatesSeen.add(statusDateKey);
              uniqueData.push(item);
            }
          }
        } else if (!seenStatuses.has(statusKey)) {
          seenStatuses.add(statusKey);
          uniqueData.push(item);
        }
      });

      setParcelData(uniqueData);
      setIsLoadingTracking(false);
    } catch (error) {
      console.log(error);
      setParcelData([]);
      setIsLoadingTracking(false);
    }
  };

  // Helper function to get status details
  const getStatusDetails = (statusCode) => {
    const statusMap = {
      2: { label: 'Arrived at Hub', icon: <HomeOutlined />, color: '#1890ff', bgColor: '#e6f7ff' },
      3: { label: 'Preparing for Delivery', icon: <CarOutlined />, color: '#fa8c16', bgColor: '#fff7e6' },
      4: { label: 'Delivered', icon: <CheckCircleFilled />, color: '#52c41a', bgColor: '#f6ffed' },
      5: { label: 'Delivery Failed', icon: <CloseCircleFilled />, color: '#f5222d', bgColor: '#fff1f0' },
      6: { label: 'Return to Sender', icon: <RocketOutlined />, color: '#722ed1', bgColor: '#f9f0ff' },
      7: { label: 'Arrived at Warehouse', icon: <HomeOutlined />, color: '#13c2c2', bgColor: '#e6fffb' },
      8: { label: 'In Transit to Hub', icon: <RocketOutlined />, color: '#1890ff', bgColor: '#e6f7ff' },
    };
    return statusMap[statusCode] || { label: 'Unknown Status', icon: <ClockCircleOutlined />, color: '#8c8c8c', bgColor: '#f5f5f5' };
  };

  const clientsSectionStyle = {
    padding: '60px 0',
  };

  const clientsContainerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 15px',
  };

  const btnCoreVal = () => {
    setCoreValue(true);
    setMissionVision(false);
    setHistory(false);
  };

  const btnMissionVision = () => {
    setCoreValue(false);
    setMissionVision(true);
    setHistory(false);
  };

  const btnHistory = () => {
    setCoreValue(false);
    setMissionVision(false);
    setHistory(true);
  };

  return (
    <div>
      <header id="header" className="fixed-top d-flex align-items-center">
        <div className="container d-flex align-items-center">
          <a href="/" className="logo me-auto">
            <img src="assets/img/SPEED-Logo.png" alt="" />
          </a>
          <nav id="navbar" className="navbar order-last order-lg-0">
            <ul>
              <li>
                <a className="nav-link scrollto active" href="#hero">
                  Home
                </a>
              </li>
              <li>
                <a className="nav-link scrollto" href="#about">
                  About Us
                </a>
              </li>
              <li>
                <a className="nav-link scrollto" href="#hero">
                  Tracking
                </a>
              </li>
              <li>
                <a className="nav-link scrollto" href="#contact">
                  Contact Us
                </a>
              </li>
            </ul>
            <i className="bi bi-list mobile-nav-toggle"></i>
          </nav>
        </div>
      </header>
      <section id="hero" className="d-flex align-items-center">
        <div className="container">
          <div className="row">
            <div className="col-xl-6">
              <h1>DOOR TO DOOR DELIVERY</h1>
              <h2>
                We make it our business to support your business. Our team of
                delivery experts are available to help you transport your goods
                on time.
              </h2>

              {/* Quick Track Box - Hero Section */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                padding: '25px',
                marginTop: '30px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)'
              }}>
                <h3 style={{
                  fontSize: '1.1rem',
                  marginBottom: '15px',
                  color: '#1c1c1c',
                  fontWeight: '600',
                  textAlign: 'left'
                }}>
                  📦 Track Your Package
                </h3>
                <form onSubmit={handleHeroSubmit} style={{ marginBottom: '0' }}>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      name="tracking_num"
                      className="form-control"
                      placeholder="Enter Tracking Number"
                      style={{
                        flex: '1',
                        minWidth: '200px',
                        color: '#333',
                        fontSize: '15px',
                        padding: '12px 18px',
                        borderRadius: '8px',
                        border: '2px solid #e0e0e0',
                        transition: 'all 0.3s ease',
                        outline: 'none'
                      }}
                      value={tracking_num}
                      onChange={(e) => setTrackingNum(e.target.value)}
                      onFocus={(e) => e.target.style.borderColor = '#dc3545'}
                      onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                      required
                    />
                    <button
                      type="submit"
                      className="btn"
                      style={{
                        padding: '12px 30px',
                        fontSize: '15px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                        color: 'white',
                        border: 'none',
                        boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(220, 53, 69, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.3)';
                      }}
                    >
                      Track Now
                    </button>
                  </div>
                </form>
                <p style={{
                  fontSize: '12px',
                  color: '#666',
                  marginTop: '10px',
                  marginBottom: '0',
                  textAlign: 'left'
                }}>
                  Enter your tracking number
                </p>
              </div>

              {/* We're Improving Banner */}
              <div style={{
                background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                border: '2px solid #ffc107',
                borderRadius: '12px',
                padding: '20px 25px',
                marginTop: '20px',
                textAlign: 'left',
                boxShadow: '0 4px 15px rgba(255, 193, 7, 0.2)'
              }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#856404',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  📢 We're Committed to Excellence
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: '#856404',
                  marginBottom: '0',
                  lineHeight: '1.6'
                }}>
                  We know we've had challenges. We're investing in new technology, training, and processes to deliver the reliable service you deserve.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section style={{
        background: '#ffffff',
        padding: '60px 0',
        borderTop: '1px solid #f0f0f0'
      }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center mb-4">
              <h2 style={{
                fontSize: 'clamp(24px, 5vw, 32px)',
                fontWeight: '700',
                color: '#1c1c1c',
                marginBottom: '15px'
              }}>
                What We Offer
              </h2>
              <p style={{
                fontSize: 'clamp(14px, 3vw, 16px)',
                color: '#666',
                maxWidth: '600px',
                margin: '0 auto',
                padding: '0 15px'
              }}>
                Reliable delivery solutions tailored to your needs
              </p>
            </div>
          </div>
          <div className="row text-center mt-4">
            <div className="col-md-6 col-lg-3 mb-4">
              <div style={{ padding: '20px' }}>
                <RocketOutlined style={{ fontSize: '48px', color: '#dc3545', marginBottom: '15px' }} />
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1c1c1c', marginBottom: '10px' }}>
                  Door-to-Door Logistics
                </h4>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                  We guarantee fast, secure and reliable door to door delivery service across Metro Manila and surrounding areas.
                </p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <div style={{ padding: '20px' }}>
                <EnvironmentOutlined style={{ fontSize: '48px', color: '#dc3545', marginBottom: '15px' }} />
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1c1c1c', marginBottom: '10px' }}>
                  Real-Time Tracking
                </h4>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                  Track your packages in real-time with our advanced tracking system. Know exactly where your delivery is.
                </p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <div style={{ padding: '20px' }}>
                <CheckCircleFilled style={{ fontSize: '48px', color: '#dc3545', marginBottom: '15px' }} />
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1c1c1c', marginBottom: '10px' }}>
                  Cash on Delivery
                </h4>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                  We provide your customers with convenient COD payment options for their purchases.
                </p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <div style={{ padding: '20px' }}>
                <CheckCircleFilled style={{ fontSize: '48px', color: '#dc3545', marginBottom: '15px' }} />
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1c1c1c', marginBottom: '10px' }}>
                  DPO Registered
                </h4>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                  Fully registered with Data Privacy Office and Data Processing System for your peace of mind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div style={{
        height: '80px',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '200px',
          height: '3px',
          background: 'linear-gradient(90deg, transparent 0%, #dc3545 50%, transparent 100%)'
        }}></div>
      </div>

      {/* Why Choose Us Section */}
      <section style={{
        background: '#f8f9fa',
        padding: '60px 0'
      }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center mb-4">
              <h2 style={{
                fontSize: 'clamp(24px, 5vw, 32px)',
                fontWeight: '700',
                color: '#1c1c1c',
                marginBottom: '15px'
              }}>
                Why Choose Speedworks
              </h2>
              <p style={{
                fontSize: 'clamp(14px, 3vw, 16px)',
                color: '#666',
                maxWidth: '600px',
                margin: '0 auto',
                padding: '0 15px'
              }}>
                Your trusted partner for fast and reliable deliveries
              </p>
            </div>
          </div>
          <div className="row text-center mt-4">
            <div className="col-md-6 col-lg-3 mb-4">
              <div style={{ padding: '20px' }}>
                <ClockCircleOutlined style={{ fontSize: '48px', color: '#dc3545', marginBottom: '15px' }} />
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1c1c1c', marginBottom: '10px' }}>
                  Established Service
                </h4>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                  Years of experience serving Metro Manila businesses with dedication and professionalism.
                </p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <div style={{ padding: '20px' }}>
                <EnvironmentOutlined style={{ fontSize: '48px', color: '#dc3545', marginBottom: '15px' }} />
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1c1c1c', marginBottom: '10px' }}>
                  Wide Coverage
                </h4>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                  Extensive coverage across Metro Manila and nearby provinces. We reach where you need us.
                </p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <div style={{ padding: '20px' }}>
                <CarOutlined style={{ fontSize: '48px', color: '#dc3545', marginBottom: '15px' }} />
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1c1c1c', marginBottom: '10px' }}>
                  Business & Personal
                </h4>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                  Serving both business clients and individual customers with the same level of care and attention.
                </p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <div style={{ padding: '20px' }}>
                <CheckCircleFilled style={{ fontSize: '48px', color: '#dc3545', marginBottom: '15px' }} />
                <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#1c1c1c', marginBottom: '10px' }}>
                  Fully Licensed
                </h4>
                <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                  Licensed courier service registered with proper government authorities for your peace of mind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section style={{
        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
        padding: '60px 0',
        color: 'white'
      }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center">
              <h2 style={{
                fontSize: 'clamp(24px, 5vw, 36px)',
                fontWeight: '700',
                color: 'white',
                marginBottom: '20px'
              }}>
                Ready to ship with Speedworks?
              </h2>
              <p style={{
                fontSize: 'clamp(14px, 3vw, 18px)',
                color: 'rgba(255, 255, 255, 0.9)',
                maxWidth: '700px',
                margin: '0 auto 30px auto',
                lineHeight: '1.6',
                padding: '0 15px'
              }}>
                Get fast, reliable delivery service today. Whether you're a business or individual, we're here to help move your packages safely and on time.
              </p>
              <div style={{
                display: 'flex',
                gap: '20px',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '30px'
              }}>
                <a
                  href="#contact"
                  style={{
                    padding: '15px 40px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#dc3545',
                    border: 'none',
                    textDecoration: 'none',
                    display: 'inline-block',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
                  }}
                >
                  Get a Quote
                </a>
                <a
                  href="#hero"
                  style={{
                    padding: '15px 40px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    background: 'transparent',
                    color: 'white',
                    border: '2px solid white',
                    textDecoration: 'none',
                    display: 'inline-block',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = '#dc3545';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'white';
                  }}
                >
                  Track Your Package
                </a>
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: '600',
                marginTop: '20px'
              }}>
                <span style={{ opacity: '0.9' }}>Call us now:</span> (02) 8952-7411
              </div>
            </div>
          </div>
        </div>
      </section>

      <main id="main">
        <section id="clients" className="clients" style={clientsSectionStyle}>
          <div style={clientsContainerStyle}>
            <div className="row justify-content-center align-items-center g-3 g-md-4">
              <div className="col-6 col-md-4 col-lg-2">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', padding: '15px' }}>
                  <img
                    src="assets/img/clients/Picture2.png"
                    alt="BPI Client Logo"
                    style={{
                      filter: 'grayscale(100%)',
                      height: 'auto',
                      maxWidth: '100%',
                      width: '100%',
                      maxHeight: '80px',
                      objectFit: 'contain',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'grayscale(0%)'}
                    onMouseLeave={(e) => e.target.style.filter = 'grayscale(100%)'}
                  />
                </div>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', padding: '15px' }}>
                  <img
                    src="assets/img/clients/Picture1.png"
                    alt="E-commerce Client Logo"
                    style={{
                      filter: 'grayscale(100%)',
                      height: 'auto',
                      maxWidth: '100%',
                      width: '100%',
                      maxHeight: '80px',
                      objectFit: 'contain',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'grayscale(0%)'}
                    onMouseLeave={(e) => e.target.style.filter = 'grayscale(100%)'}
                  />
                </div>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', padding: '15px' }}>
                  <img
                    src="assets/img/clients/Picture2.png"
                    alt="BPI Client Logo"
                    style={{
                      filter: 'grayscale(100%)',
                      height: 'auto',
                      maxWidth: '100%',
                      width: '100%',
                      maxHeight: '80px',
                      objectFit: 'contain',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'grayscale(0%)'}
                    onMouseLeave={(e) => e.target.style.filter = 'grayscale(100%)'}
                  />
                </div>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', padding: '15px' }}>
                  <img
                    src="assets/img/clients/Picture1.png"
                    alt="E-commerce Client Logo"
                    style={{
                      filter: 'grayscale(100%)',
                      height: 'auto',
                      maxWidth: '100%',
                      width: '100%',
                      maxHeight: '80px',
                      objectFit: 'contain',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'grayscale(0%)'}
                    onMouseLeave={(e) => e.target.style.filter = 'grayscale(100%)'}
                  />
                </div>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', padding: '15px' }}>
                  <img
                    src="assets/img/clients/Picture2.png"
                    alt="BPI Client Logo"
                    style={{
                      filter: 'grayscale(100%)',
                      height: 'auto',
                      maxWidth: '100%',
                      width: '100%',
                      maxHeight: '80px',
                      objectFit: 'contain',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'grayscale(0%)'}
                    onMouseLeave={(e) => e.target.style.filter = 'grayscale(100%)'}
                  />
                </div>
              </div>
              <div className="col-6 col-md-4 col-lg-2">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100%', padding: '15px' }}>
                  <img
                    src="assets/img/clients/Picture1.png"
                    alt="E-commerce Client Logo"
                    style={{
                      filter: 'grayscale(100%)',
                      height: 'auto',
                      maxWidth: '100%',
                      width: '100%',
                      maxHeight: '80px',
                      objectFit: 'contain',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.filter = 'grayscale(0%)'}
                    onMouseLeave={(e) => e.target.style.filter = 'grayscale(100%)'}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        <section id="about" className="about section-bg">
          <div className="container">
            <div className="row no-gutters">
              <div className="content col-xl-5 d-flex align-items-stretch">
                <div className="content">
                  <h3 style={{ color: 'white' }}>
                    With Door-to-Door Logistics
                  </h3>
                  <p>
                    We guarantee fast, secure and reliable door to door delivery
                    service.
                  </p>
                  <h3 style={{ color: 'white' }}>Cash on Delivery Service</h3>
                  <p>We provide your customers with a COD payment.</p>
                  <a href="#contact" className="about-btn" style={{
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    minHeight: '48px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease'
                  }}>
                    <span>CONTACT US</span> <RightOutlined />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section id="tabs" className="tabs">
          <div className="container">
            <ul className="nav nav-tabs row d-flex">
              <li className="nav-item col-3">
                <a
                  className={
                    mission_vision ? 'nav-link active show' : 'nav-link'
                  }
                >
                  <i className="ri-gps-line"></i>
                  <h4
                    className="d-none d-lg-block"
                    onClick={() => {
                      btnMissionVision();
                    }}
                  >
                    Mission & Vision
                  </h4>
                </a>
              </li>
              <li className="nav-item col-3">
                <a className={core_value ? 'nav-link active show' : 'nav-link'}>
                  <i className="ri-body-scan-line"></i>
                  <h4
                    className="d-none d-lg-block"
                    onClick={() => {
                      btnCoreVal();
                    }}
                  >
                    Core Values
                  </h4>
                </a>
              </li>
              <li className="nav-item col-3">
                <a className={history ? 'nav-link active show' : 'nav-link'}>
                  <i className="ri-sun-line"></i>
                  <h4
                    className="d-none d-lg-block"
                    onClick={() => {
                      btnHistory();
                    }}
                  >
                    History
                  </h4>
                </a>
              </li>
            </ul>
            <div className="tab-content">
              <div className="tab-pane active show" id="tab-1">
                <div className="row">
                  <div className="col-lg-6 order-2 order-lg-1 mt-3 mt-lg-0">
                    <h3>
                      {mission_vision
                        ? 'Mission Vision'
                        : core_value
                        ? 'Core Values'
                        : history
                        ? 'History'
                        : ''}
                    </h3>
                    {mission_vision ? (
                      <>
                        <ul>
                          <li>
                            {/* <i className="ri-check-double-line"></i> */}
                            It is our commitment to provide exceptional courier
                            service to our clients with passion and integrity.
                          </li>
                        </ul>
                        <ul>
                          <li>
                            {/* <i className="ri-check-double-line"></i> */}
                            We set the standards to courier perfection.{' '}
                          </li>
                        </ul>
                      </>
                    ) : core_value ? (
                      <>
                        <ul>
                          <li>
                            {/* <i className="ri-check-double-line"></i> */}
                            Go Above and Beyond – We strive to exceed the
                            expectations of the people we work with and the
                            clients we serve. From taking ownership of
                            responsibilities outside of our job scope, to
                            remaining committed to a venture from start to
                            finish.
                          </li>
                        </ul>
                        <ul>
                          <li>
                            {/* <i className="ri-check-double-line"></i> */}
                            Our Team - Our successful teamwork is achieved by
                            holding on to individual strengths and overcoming
                            differences to produce the most favorable results.
                            Working in this way builds trust and respect for
                            each team member and maximizes productivity.
                          </li>
                        </ul>
                        <ul>
                          <li>
                            {/* <i className="ri-check-double-line"></i> */}
                            Trust and Integrity – We exert all our efforts with
                            honesty and integrity at all times. We are
                            transparent and trustworthy with our employees and
                            clients. Setting a good example by knowing and doing
                            what is right inside and outside of the
                            organization.
                          </li>
                        </ul>
                      </>
                    ) : history ? (
                      <>
                        <ul>
                          <li>
                            Speedworks Courier Services Corporation is a private
                            company established in January 1996 by proprietor
                            Marfred G. Samonte with the purpose of providing
                            messengerial service to private and public sectors.
                          </li>
                        </ul>
                        <ul>
                          <li>
                            {/* <i className="ri-check-double-line"></i> */}
                            With an outstanding team committed to perfecting the
                            craft, the company flourished and became a
                            corporation in 2015.
                          </li>
                        </ul>
                        <ul>
                          <li>
                            {/* <i className="ri-check-double-line"></i> */}
                            Today, the company continues to provide top notch
                            quality service to its clients. With its dedicated
                            team and branch locations throughout NCR, Speedworks
                            Courier Services Corporation is your premium service
                            of choice.
                          </li>
                        </ul>
                      </>
                    ) : (
                      ''
                    )}
                  </div>
                  <div className="col-lg-6 order-1 order-lg-2 text-center">
                    <img src="assets/tabs-1.jpg" alt="" className="img-fluid" />
                    <img
                      src="assets/missionvision.jpg"
                      alt=""
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>

              <div className="tab-pane" id="tab-2">
                <div className="row">
                  <div className="col-lg-6 order-2 order-lg-1 mt-3 mt-lg-0">
                    <h3>Go Above and Beyond</h3>
                    <br />
                    <p>
                      We strive to exceed the expectations of the people we work
                      with and the clients we serve. From taking ownership of
                      responsibilities outside of our job scope, to remaining
                      committed to a venture from start to finish.
                      <br />
                      <br />
                      We go above and beyond to make a positive impact on what
                      we do.
                    </p>
                    <ul>
                      <li>
                        <i className="ri-check-double-line"></i> Trust and
                        Integrity – We exert all our efforts with honesty and{' '}
                        <br /> integrity at all times.
                      </li>
                      <li>
                        <i className="ri-check-double-line"></i> We are
                        transparent and trustworthy with our employees and
                        clients.
                      </li>
                      <li>
                        <i className="ri-check-double-line"></i> Setting a Good
                        example by knowing and doing what is right inside <br />
                        and outside of the organization.
                      </li>
                    </ul>
                  </div>
                  <div className="col-lg-6 order-1 order-lg-2 text-center">
                    <img src="assets/tabs-2.jpg" alt="" className="img-fluid" />
                    <img
                      src="assets/corevalues.jpg"
                      alt=""
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>

              <div className="tab-pane" id="tab-3">
                <div className="row">
                  <div className="col-lg-6 order-2 order-lg-1 mt-3 mt-lg-0">
                    <h3>History</h3>
                    <br/><br />
                    <p>Speedworks Courier Services Corporation is a private
                      company established in January 1996 by proprietor Marfred
                      G. Samonte with the purpose of providing messengerial
                      service to private, government and commercial companies.{' '}
                      <br /> <br />
                      With an outstanding team committed to perfecting the
                      craft, the company flourished and became a corporation in
                      2015.
                    </p>
                    <p>
                      Today, the company continues to provide top notch quality
                      service to its clients. With its dedicated team and branch
                      locations throughout NCR, Speedworks Courier Services
                      Corporation is your premium service of choice.
                    </p>
                  </div>
                  <div className="col-lg-6 order-1 order-lg-2 text-center">
                    <img
                      src="assets/history.jpg"
                      alt=""
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section id="faq" className="faq" style={{ padding: 'clamp(40px, 10vw, 80px) 0', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
          <div className="container">
            <div className="section-title" style={{ marginBottom: '20px' }}>
              <h2 style={{
                fontSize: 'clamp(20px, 5vw, 40px)',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '15px',
                paddingBottom: '15px',
                color: '#1c1c1c',
                padding: '0 15px 15px 15px'
              }}>🌏 Delivery Coverage Area</h2>
              <p style={{
                fontSize: 'clamp(14px, 3vw, 16px)',
                color: '#666',
                maxWidth: '600px',
                margin: '0 auto 20px',
                padding: '0 15px'
              }}>
                Fast, reliable delivery across Metro Manila and nearby provinces
              </p>
            </div>

            <div className="row justify-content-center align-items-stretch" style={{ marginBottom: '10px' }}>
              {/* Combined Coverage Areas Card */}
              <div className="col-lg-6 col-md-12" style={{ marginBottom: '20px' }}>
                <div style={{
                  background: 'white',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(220, 53, 69, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 15px 50px rgba(220, 53, 69, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.12)';
                }}
                >
                  {/* Metro Manila Section */}
                  <div style={{
                    background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                    padding: '25px',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-50px',
                      right: '-50px',
                      width: '150px',
                      height: '150px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%'
                    }}></div>
                    <h3 style={{
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '1.5rem',
                      marginBottom: '0',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      🏙️ Metro Manila
                    </h3>
                  </div>

                  {/* Metro Manila Cities Grid */}
                  <div style={{ padding: '20px' }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '8px'
                    }}>
                      {['Caloocan', 'Las Piñas', 'Malabon', 'Mandaluyong', 'Manila', 'Muntinlupa',
                        'Navotas', 'Novaliches', 'Paranaque', 'Pateros', 'Quezon City', 'Taguig', 'Valenzuela'].map((city, index) => (
                        <div key={index} style={{
                          padding: '12px 16px',
                          background: '#f8f9fa',
                          borderRadius: '10px',
                          fontSize: '14px',
                          color: '#495057',
                          fontWeight: '500',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          border: '2px solid transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#fff';
                          e.currentTarget.style.borderColor = '#dc3545';
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f8f9fa';
                          e.currentTarget.style.borderColor = 'transparent';
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        >
                          <span style={{ color: '#dc3545', fontSize: '16px' }}>📍</span>
                          {city}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Provincial Section Header */}
                  <div style={{
                    background: 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                    padding: '25px',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    marginTop: '10px'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-50px',
                      left: '-50px',
                      width: '150px',
                      height: '150px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%'
                    }}></div>
                    <h3 style={{
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '1.5rem',
                      marginBottom: '0',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      🌄 Provincial Areas
                    </h3>
                  </div>

                  {/* Provinces */}
                  <div style={{ padding: '20px' }}>
                    {['Batangas', 'Bulacan', 'Cavite', 'Laguna'].map((province, index) => (
                      <div key={index} style={{
                        padding: '18px 20px',
                        background: '#f8f9fa',
                        borderRadius: '12px',
                        fontSize: '15px',
                        color: '#495057',
                        fontWeight: '500',
                        marginBottom: '10px',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        border: '2px solid transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.borderColor = '#6c757d';
                        e.currentTarget.style.transform = 'translateX(5px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(108, 117, 125, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f8f9fa';
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '20px' }}>🗺️</span>
                          {province}
                        </span>
                        <span style={{ fontSize: '20px', opacity: 0.3 }}>→</span>
                      </div>
                    ))}
                  </div>

                </div>
              </div>

              {/* Map Card */}
              <div className="col-lg-6 col-md-12" style={{ marginBottom: '20px' }}>
                <div style={{
                  background: 'white',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(220, 53, 69, 0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 15px 50px rgba(220, 53, 69, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.12)';
                }}
                >
                  {/* Header */}
                  <div style={{
                    background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
                    padding: '25px',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-50px',
                      right: '-50px',
                      width: '150px',
                      height: '150px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%'
                    }}></div>
                    <h3 style={{
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '1.5rem',
                      marginBottom: '8px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      🗺️ Coverage Map
                    </h3>
                    <div style={{
                      display: 'inline-block',
                      background: 'rgba(255, 255, 255, 0.25)',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      color: 'white',
                      fontWeight: '600',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      📍 Our Hubs & Routes
                    </div>
                  </div>

                  {/* Advanced Map Image */}
                  <div style={{
                    padding: '20px 20px 10px 20px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Map Container with Advanced Styling */}
                    <div style={{
                      position: 'relative',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      marginBottom: '0',
                      border: '4px solid transparent',
                      backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #28a745, #20c997, #17a2b8, #28a745)',
                      backgroundOrigin: 'border-box',
                      backgroundClip: 'padding-box, border-box',
                      boxShadow: '0 12px 40px rgba(40, 167, 69, 0.35), 0 4px 16px rgba(32, 201, 151, 0.25), inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                    }}>
                      {/* Map Image */}
                      <div style={{
                        position: 'relative',
                        height: '500px',
                        overflow: 'hidden'
                      }}>
                        <img
                          src="assets/branches.png"
                          className="img-fluid"
                          alt="Speedworks Coverage Map"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            filter: 'brightness(1.1) contrast(1.2) saturate(1.4) hue-rotate(-5deg)'
                          }}
                        />

                        {/* Dark Overlay with Grid Pattern */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `
                            linear-gradient(90deg, rgba(40, 167, 69, 0.12) 2px, transparent 2px),
                            linear-gradient(rgba(40, 167, 69, 0.12) 2px, transparent 2px)
                          `,
                          backgroundSize: '50px 50px',
                          pointerEvents: 'none',
                          opacity: 0.6
                        }}></div>

                        {/* Gradient Overlay for Depth */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(to bottom, rgba(40, 167, 69, 0.2) 0%, transparent 15%, transparent 85%, rgba(0, 0, 0, 0.3) 100%)',
                          pointerEvents: 'none'
                        }}></div>

                        {/* Corner Glow Effects */}
                        <div style={{
                          position: 'absolute',
                          top: '-100px',
                          right: '-100px',
                          width: '300px',
                          height: '300px',
                          background: 'radial-gradient(circle, rgba(32, 201, 151, 0.3) 0%, transparent 70%)',
                          pointerEvents: 'none',
                          filter: 'blur(40px)'
                        }}></div>
                        <div style={{
                          position: 'absolute',
                          bottom: '-100px',
                          left: '-100px',
                          width: '300px',
                          height: '300px',
                          background: 'radial-gradient(circle, rgba(23, 162, 184, 0.3) 0%, transparent 70%)',
                          pointerEvents: 'none',
                          filter: 'blur(40px)'
                        }}></div>
                      </div>
                    </div>

                    {/* Simple Statistics Text */}
                    <div style={{
                      marginTop: '15px',
                      padding: '12px 20px',
                      background: 'rgba(40, 167, 69, 0.05)',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '14px',
                      color: '#495057',
                      fontWeight: '500'
                    }}>
                      📍 <strong style={{ color: '#28a745' }}>13 Cities</strong> •
                      🗺️ <strong style={{ color: '#20c997' }}>4 Provinces</strong> •
                      📏 <strong style={{ color: '#17a2b8' }}>100K+ Sq Km</strong>
                    </div>

                    {/* Service Info & Contact */}
                    <div style={{
                      marginTop: '20px',
                      padding: '20px',
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      borderRadius: '12px',
                      border: '2px solid #28a745',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        color: '#495057',
                        lineHeight: '1.8',
                        marginBottom: '15px'
                      }}>
                        <div style={{ marginBottom: '8px', fontWeight: '500' }}>
                          🚚 <span style={{ color: '#6c757d' }}>Serving Metro Manila & nearby provinces</span>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          📞 <strong style={{ color: '#dc3545' }}>Questions? Call (02) 8952-7411</strong>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          📧 <span style={{ color: '#6c757d' }}>Email: info@speedworkscourier.com</span>
                        </div>
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#ffffff',
                        fontWeight: '600',
                        padding: '15px 20px',
                        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                        borderRadius: '10px',
                        border: '2px solid #1e7e34',
                        boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}>
                        <span style={{ fontSize: '18px' }}>💪</span>
                        <span>We're committed to improving our service every day</span>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
        <section id="contact" className="contact">
          <div className="container" data-aos="fade-up">
            <div className="section-title">
              <h2 style={{ fontSize: 'clamp(28px, 5vw, 36px)', fontWeight: '700', marginBottom: '15px' }}>CONTACT US</h2>
              <p style={{ fontSize: 'clamp(16px, 3vw, 18px)', color: '#666', marginBottom: '10px', padding: '0 15px' }}>
                Ready to ship with Speedworks? We're here to help!
              </p>
              <p style={{ fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#999', fontStyle: 'italic', padding: '0 15px' }}>
                Response time: Within 24 hours • Mondays to Saturdays 8:00AM - 5:00PM
              </p>
            </div>
            <div className="row">
              <div className="col-lg-6">
                <div className="row" style={{ alignItems: 'stretch' }}>
                  <div className="col-md-12" style={{ marginBottom: '20px', display: 'flex' }}>
                    <div className="info-box" style={{
                      background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                      border: '2px solid #e0e0e0',
                      borderRadius: '12px',
                      padding: 'clamp(20px, 4vw, 30px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      minHeight: '200px',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(220, 53, 69, 0.15)';
                      e.currentTarget.style.borderColor = '#dc3545';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                      <i className="ri-map-pin-fill" style={{
                        boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                        color: 'white',
                        width: '60px',
                        height: '60px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '12px',
                        fontSize: '28px',
                        marginBottom: '20px',
                        lineHeight: '1'
                      }}></i>
                      <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1c1c1c', marginBottom: '15px' }}>Main Office</h3>
                      <p style={{ fontSize: '15px', color: '#666', lineHeight: '1.6', margin: 0 }}>
                        SMS Bldg, #15 Jade Street Pilar Village, Las Piñas City 1740
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6" style={{ marginBottom: '20px', display: 'flex' }}>
                    <div className="info-box" style={{
                      background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                      border: '2px solid #e0e0e0',
                      borderRadius: '12px',
                      padding: 'clamp(20px, 3vw, 25px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      minHeight: '200px',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(220, 53, 69, 0.15)';
                      e.currentTarget.style.borderColor = '#dc3545';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                      <i className="ri-mail-fill" style={{
                        boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                        color: 'white',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '10px',
                        fontSize: '24px',
                        marginBottom: '15px',
                        lineHeight: '1'
                      }}></i>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1c1c1c', marginBottom: '12px' }}>Email Us</h3>
                      <a href="mailto:info@speedworkscourier.com" style={{
                        fontSize: '14px',
                        color: '#dc3545',
                        textDecoration: 'none',
                        fontWeight: '600',
                        display: 'block',
                        marginBottom: '8px'
                      }}>info@speedworkscourier.com</a>
                      <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>We'll respond within 24 hours</p>
                    </div>
                  </div>
                  <div className="col-md-6" style={{ marginBottom: '20px', display: 'flex' }}>
                    <div className="info-box" style={{
                      background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                      border: '2px solid #e0e0e0',
                      borderRadius: '12px',
                      padding: 'clamp(20px, 3vw, 25px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      minHeight: '200px',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(220, 53, 69, 0.15)';
                      e.currentTarget.style.borderColor = '#dc3545';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                      <i className="ri-phone-fill" style={{
                        boxShadow: '0 4px 12px rgba(23, 162, 184, 0.3)',
                        background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                        color: 'white',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '10px',
                        fontSize: '24px',
                        marginBottom: '15px',
                        lineHeight: '1'
                      }}></i>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1c1c1c', marginBottom: '12px' }}>Call Us</h3>
                      <a href="tel:0289527411" style={{
                        fontSize: '16px',
                        color: '#dc3545',
                        textDecoration: 'none',
                        fontWeight: '700',
                        display: 'block'
                      }}>(02) 8952-7411</a>
                      <a href="tel:0288068353" style={{
                        fontSize: '16px',
                        color: '#dc3545',
                        textDecoration: 'none',
                        fontWeight: '700',
                        display: 'block',
                        marginBottom: '8px'
                      }}>(02) 8806-8353</a>
                      <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>Mondays to Saturdays 8:00AM - 5:00PM</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div style={{
                  background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(220, 53, 69, 0.15)';
                  e.currentTarget.style.borderColor = '#dc3545';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1c1c1c',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <i className="ri-map-pin-2-fill" style={{ color: '#dc3545', fontSize: '24px' }}></i>
                    Find Us Here
                  </h3>
                  <div style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7728.138377432466!2d121.006938!3d14.423175!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x54847bf84f91df31!2sSpeedworks%20Courier%20Services%20Corporation!5e0!3m2!1sen!2sph!4v1627284221901!5m2!1sen!2sph"
                      width="100%"
                      height="400"
                      style={{ border: 0, display: 'block' }}
                      allowfullscreen=""
                      loading="lazy"
                    ></iframe>
                  </div>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=Speedworks+Courier+Services+Corporation,+15+Jade+Street+Pilar+Village,+Las+Piñas+City+1740"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      marginTop: '15px',
                      padding: '12px 20px',
                      background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                      color: 'white',
                      textAlign: 'center',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '14px',
                      boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.3)';
                    }}
                  >
                    <i className="ri-navigation-fill" style={{ marginRight: '8px' }}></i>
                    Get Directions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <footer id="footer">
        <div className="footer-top">
          <div className="container">
            <div className="row">
              <div className="col-lg-3 col-md-6 footer-contact" style={{ marginBottom: '30px' }}>
                <h3 style={{
                  color: 'white',
                  fontSize: '1.3rem',
                  marginBottom: '20px',
                  lineHeight: '1.4'
                }}>
                  Speedworks Courier Services Corporation<span></span>
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                  marginBottom: '15px'
                }}>
                  SMS Bldg, <br />
                  #15 Jade Street, Pilar Village,
                  <br />
                  Las Piñas City 1740 <br />
                </p>
                <p style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                  marginTop: '15px'
                }}>
                  <strong>Phone:</strong>{' '}
                  <a
                    href="tel:89527411"
                    style={{
                      color: '#fff',
                      textDecoration: 'underline',
                      padding: '5px 0',
                      display: 'inline-block'
                    }}
                  >
                    02 8952-7411
                  </a>
                  {' or '}
                  <a
                    href="tel:88068353"
                    style={{
                      color: '#fff',
                      textDecoration: 'underline',
                      padding: '5px 0',
                      display: 'inline-block'
                    }}
                  >
                    02 8806-8353
                  </a>
                  <br />
                  <strong>Email:</strong>{' '}
                  <a
                    href="mailto:info@speedworkscourier.com"
                    style={{
                      color: '#fff',
                      textDecoration: 'underline',
                      padding: '5px 0',
                      display: 'inline-block'
                    }}
                  >
                    info@speedworkscourier.com
                  </a>
                </p>
              </div>

              <div className="col-lg-2 col-md-6 footer-links" style={{ marginBottom: '30px' }}>
                <h4 style={{
                  color: 'white',
                  fontSize: '1.1rem',
                  marginBottom: '20px'
                }}>Useful Links</h4>
                <ul style={{ paddingLeft: '0' }}>
                  <li style={{ marginBottom: '12px', fontSize: '15px' }}>
                    <RightOutlined style={{ fontSize: '12px', marginRight: '8px' }} /> <a href="#" style={{ color: '#fff', textDecoration: 'none' }}>Home</a>
                  </li>
                  <li style={{ marginBottom: '12px', fontSize: '15px' }}>
                    <RightOutlined style={{ fontSize: '12px', marginRight: '8px' }} /> <a href="#" style={{ color: '#fff', textDecoration: 'none' }}>About us</a>
                  </li>
                  <li style={{ marginBottom: '12px', fontSize: '15px' }}>
                    <RightOutlined style={{ fontSize: '12px', marginRight: '8px' }} /> <a href="#" style={{ color: '#fff', textDecoration: 'none' }}>Tracking</a>
                  </li>
                  <li style={{ marginBottom: '12px', fontSize: '15px' }}>
                    <RightOutlined style={{ fontSize: '12px', marginRight: '8px' }} /> <a href="#" style={{ color: '#fff', textDecoration: 'none' }}>Terms of Service</a>
                  </li>
                  <li style={{ marginBottom: '12px', fontSize: '15px' }}>
                    <RightOutlined style={{ fontSize: '12px', marginRight: '8px' }} />{' '}
                    <a href="/privacy-policy" style={{ color: '#fff', textDecoration: 'none' }}>Privacy Notice</a>
                  </li>
                </ul>
              </div>

              <div className="col-lg-3 col-md-6 footer-links" style={{ marginBottom: '30px' }}>
                <h4 style={{
                  color: 'white',
                  fontSize: '1.1rem',
                  marginBottom: '20px'
                }}>Our Services</h4>
                <ul style={{ paddingLeft: '0' }}>
                  <li style={{ marginBottom: '12px', fontSize: '15px' }}>
                    <RightOutlined style={{ fontSize: '12px', marginRight: '8px' }} />{' '}
                    <a href="#about" style={{ color: '#fff', textDecoration: 'none' }}>Door to Door Delivery Service</a>
                  </li>
                  <li style={{ marginBottom: '12px', fontSize: '15px' }}>
                    <RightOutlined style={{ fontSize: '12px', marginRight: '8px' }} /> <a href="#about" style={{ color: '#fff', textDecoration: 'none' }}>COD Service</a>
                  </li>
                </ul>
              </div>
              <div className="col-lg-3 col-md-6 footer-links" style={{ marginBottom: '30px', textAlign: 'center' }}>
                {/* No link/modal — same light-up-on-hover treatment as the
                    client/partner logos above (grayscale -> color), not a
                    click-through. Avoids the old problem entirely: a browser
                    with no PDF viewer/extension just opened a blank tab for
                    the linked certificate. */}
                <img
                  src="/assets/img/dpo.png"
                  width={180}
                  height={180}
                  alt="NPC COR Seal Speedworks"
                  style={{
                    filter: 'grayscale(100%)',
                    maxWidth: '100%',
                    height: 'auto',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.filter = 'grayscale(0%)'}
                  onMouseLeave={(e) => e.target.style.filter = 'grayscale(100%)'}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="container d-md-flex py-4" style={{ padding: '20px 15px' }}>
          <div className="me-md-auto text-center text-md-start">
            <div className="copyright" style={{ fontSize: '14px', lineHeight: '1.6' }}>
              &copy; Copyright . All Rights Reserved
            </div>
          </div>
          <div className="social-links text-center text-md-end pt-3 pt-md-0" style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <a href="#" className="twitter" style={{
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              transition: 'all 0.3s ease'
            }}>
              <TwitterOutlined />
            </a>
            <a href="#" className="facebook" style={{
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              transition: 'all 0.3s ease'
            }}>
              <FacebookOutlined />
            </a>
            <a href="#" className="instagram" style={{
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              transition: 'all 0.3s ease'
            }}>
              <InstagramOutlined />
            </a>
            <a href="#" className="google-plus" style={{
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              transition: 'all 0.3s ease'
            }}>
              <GooglePlusOutlined />
            </a>
            <a href="#" className="linkedin" style={{
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              transition: 'all 0.3s ease'
            }}>
              <LinkedinOutlined />
            </a>
          </div>
        </div>
      </footer>

      {/* Tracking Results Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            overflowY: 'auto'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '15px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'modalFadeIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowModal(false)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'transparent',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#999',
                zIndex: 10,
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f5f5f5';
                e.target.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#999';
              }}
            >
              ✕
            </button>

            {/* Modal Content */}
            <div style={{ padding: '40px 30px' }}>
              <h2 style={{
                fontSize: '1.8rem',
                marginBottom: '10px',
                color: '#1c1c1c',
                fontWeight: '700',
                textAlign: 'center'
              }}>
                📦 Tracking Results
              </h2>
              <p style={{
                textAlign: 'center',
                color: '#666',
                marginBottom: '30px',
                fontSize: '14px'
              }}>
                Tracking Number: <strong>{tracking}</strong>
              </p>

              {/* Alert for Credit Card Deliveries */}
              {showAlert && (
                <div className="alert text-dark text-start" role="alert" style={{
                  padding: '20px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '10px',
                  marginBottom: '20px'
                }}>
                  {contentLoading ? (
                    <p>Loading tracking information...</p>
                  ) : trackingContent ? (
                    <>
                      <strong>Please note:</strong> {trackingContent.creditCardNote.replace('Please note:', '').trim()}
                      <br /><br />
                      For any inquiries, please contact us at:
                      <br />
                      <ul className="list-group text-start" style={{ fontSize: '14px', listStyle: 'none', paddingLeft: 0 }}>
                        <li style={{ padding: '8px 0' }}>Facebook: <a href={trackingContent.contactFacebook} style={{ textDecoration: 'underline', color: '#0066cc', padding: '4px' }}>speedworkscourier</a></li>
                      </ul>
                      <br />
                      <strong>Response Schedule:</strong> Monday to Saturday, 8:00 AM–5:00 PM. Inquiries are responded to within 24 hours during business days.
                      <br /><br />
                      Thank you for your understanding.
                    </>
                  ) : (
                    <>
                      <strong>Please note:</strong> For credit card deliveries, the bank does not provide us with your contact number. As a result, we are unable to notify you directly about your delivery status.
                      <br /><br />
                      For any inquiries, please contact us at:
                      <br />
                      <ul className="list-group text-start" style={{ fontSize: '14px', listStyle: 'none', paddingLeft: 0 }}>
                        <li style={{ padding: '8px 0' }}>Facebook: <a href="https://www.facebook.com/speedworkscourier" style={{ textDecoration: 'underline', color: '#0066cc', padding: '4px' }}>speedworkscourier</a></li>
                      </ul>
                      <br />
                      <strong>Response Schedule:</strong> Monday to Saturday, 8:00 AM–5:00 PM. Inquiries are responded to within 24 hours during business days.
                      <br /><br />
                      Thank you for your understanding.
                    </>
                  )}
                </div>
              )}

              {/* System Enhancement Notice */}
              <div style={{
                padding: '15px 20px',
                fontSize: '13px',
                lineHeight: '1.5',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                border: '1px solid #90caf9',
                borderRadius: '10px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                textAlign: 'left'
              }}>
                <span style={{ fontSize: '18px' }}>ℹ️</span>
                <div style={{ color: '#1565c0', textAlign: 'left' }}>
                  <strong>Notice:</strong> Our tracking system is currently undergoing enhancements, which may cause delays in status updates. We apologize for the inconvenience.
                </div>
              </div>

              {/* Tracking Timeline */}
              {isLoadingTracking ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#666'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #dc3545',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 20px'
                  }}></div>
                  <p style={{ fontSize: '16px', fontWeight: '500' }}>Fetching tracking information...</p>
                  <p style={{ fontSize: '13px', color: '#999', marginTop: '10px' }}>This usually takes 2-3 seconds</p>
                </div>
              ) : parcelData && parcelData.length > 0 ? (
                <div style={{
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  overflow: 'hidden'
                }}>
                  {/* Header */}
                  <div style={{
                    background: 'linear-gradient(135deg, #e53935 0%, #d32f2f 100%)',
                    padding: '20px',
                    marginBottom: '15px',
                    borderBottom: '2px solid #c62828'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '15px'
                    }}>
                      <div>
                        <div style={{ fontSize: '11px', color: '#fff', marginBottom: '4px', textTransform: 'uppercase', textAlign: 'left', fontWeight: '600', letterSpacing: '0.5px', opacity: 0.9 }}>
                          Tracking Number
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#fff', textAlign: 'left' }}>
                          {parcelData[0]?.awb_number || parcelData[0]?.delivery?.awb_number}
                        </div>
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '5px'
                        }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#fff',
                            boxShadow: '0 0 8px rgba(255, 255, 255, 0.6)'
                          }}></div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#fff', opacity: 0.9 }}>
                            Current Status
                          </div>
                        </div>
                        <div style={{
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          opacity: 0.95,
                          marginBottom: '5px'
                        }}>
                          {getStatusDetails(parcelData[0]?.status_delivery).icon}
                          {getStatusDetails(parcelData[0]?.status_delivery).label}
                        </div>
                        <div style={{ fontSize: '11px', color: '#fff', textAlign: 'left', opacity: 0.85 }}>
                          {moment.utc(parcelData[0]?.date).utcOffset('+08:00').format('MMM D, YYYY')}
                        </div>
                        {parcelData[0]?.status_delivery === 2 && parcelData[0]?.delivery?.hub?.name && (
                          <div style={{ fontSize: '11px', color: '#fff', textAlign: 'left', opacity: 0.85 }}>
                            {parcelData[0]?.delivery?.hub?.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div style={{ paddingLeft: '20px', paddingRight: '20px', paddingBottom: '20px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1c1c1c', marginBottom: '15px', textAlign: 'left' }}>
                      Delivery Journey
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{
                        position: 'absolute',
                        left: '6px',
                        top: '12px',
                        bottom: '12px',
                        width: '2px',
                        background: '#e0e0e0',
                        zIndex: 0
                      }}></div>

                      {/* Each entry (`res`) also carries a `reason` field from the
                          API (the failed-delivery reason, when applicable) that is
                          intentionally not read or rendered anywhere in this timeline
                          — some reasons are internal operational notes, not meant for
                          a customer to see. If a future version of this page ever
                          displays the reason, it needs filtering for internal-only
                          reasons first (an is_public flag on the reason lookup table,
                          applied server-side before the response is built), not a
                          plain pass-through of res.reason. */}
                      {parcelData.map((res, index) => {
                        const statusDetails = getStatusDetails(res.status_delivery);
                        const isFirst = index === 0;
                        const isLast = index === parcelData.length - 1;

                        return (
                          <div key={index} style={{
                            position: 'relative',
                            paddingLeft: '28px',
                            paddingBottom: isLast ? '24px' : '16px',
                            marginBottom: isLast ? '0' : '16px',
                            borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
                            zIndex: 1
                          }}>
                            <div style={{
                              position: 'absolute',
                              left: '0',
                              top: '2px',
                              width: '14px',
                              height: '14px',
                              borderRadius: '50%',
                              background: isFirst ? statusDetails.color : '#fff',
                              border: `2px solid ${isFirst ? statusDetails.color : '#d9d9d9'}`,
                              zIndex: 2
                            }}></div>

                            <div style={{ textAlign: 'left' }}>
                              <div style={{
                                fontSize: '14px',
                                fontWeight: isFirst ? '600' : '500',
                                color: isFirst ? '#1c1c1c' : '#666',
                                marginBottom: '2px'
                              }}>
                                {statusDetails.label}
                              </div>
                              {res.status_delivery === 2 && res.delivery?.hub?.name && (
                                <div style={{ fontSize: '12px', color: '#999' }}>
                                  {res.delivery?.hub?.name}
                                </div>
                              )}
                              {res.status_delivery === 6 && (
                                <div style={{ fontSize: '12px', color: '#722ed1', fontStyle: 'italic', marginTop: '4px' }}>
                                  We kindly request you to coordinate with your bank for further assistance.
                                </div>
                              )}
                              <div style={{ fontSize: '11px', color: '#bbb', marginTop: '2px' }}>
                                {moment.utc(res.date).utcOffset('+08:00').format('MMM D, YYYY')}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#999',
                  fontSize: '14px'
                }}>
                  {parcelData === null ? 'Loading tracking information...' : 'No tracking records found for this number.'}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  onClick={() => {
                    setShowModal(false);
                    setTrackingNum('');
                    setParcelData(null);
                  }}
                  style={{
                    color: 'white',
                    background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                    borderColor: '#dc3545',
                    minHeight: '48px',
                    fontSize: '16px',
                    fontWeight: '600',
                    padding: '0 30px',
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)'
                  }}
                >
                  Track Another Package
                </Button>
                <Button
                  onClick={() => setShowModal(false)}
                  style={{
                    color: '#666',
                    background: 'white',
                    borderColor: '#ddd',
                    minHeight: '48px',
                    fontSize: '16px',
                    fontWeight: '600',
                    padding: '0 30px',
                    borderRadius: '8px'
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modalFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {/* SupportWidget temporarily hidden — verify step depends on consignee_name
          which is intentionally stripped from the public tracking API for privacy.
          Needs a dedicated backend verification endpoint before re-enabling. */}
      {/* <SupportWidget /> */}
    </div>
  );
}

export default Home;
